/**
 * PromptManager Component
 * Manages AI prompts - view history, create new, activate previous versions
 * Focus-driven UI: hides list when creating/editing
 */

import React, { useState } from 'react'
import { SparklesIcon, PlusIcon, CheckCircleIcon, ClockIcon, TrashIcon } from '@heroicons/react/24/outline'
import { usePrompts, useCreatePrompt, useActivatePrompt, useDeletePrompt } from '../hooks/useIA'

interface PromptManagerProps {
  onPromptSelect: (promptId: string, promptText: string) => void
  selectedPromptId: string | null
}

export const PromptManager: React.FC<PromptManagerProps> = ({
  onPromptSelect,
  selectedPromptId
}) => {
  const [isCreating, setIsCreating] = useState(false)
  const [newPromptText, setNewPromptText] = useState('')
  const [newPromptComment, setNewPromptComment] = useState('')
  const [showHistory, setShowHistory] = useState(false)
  const [isEditingActive, setIsEditingActive] = useState(false)
  const [editedPromptText, setEditedPromptText] = useState('')
  const [editedPromptComment, setEditedPromptComment] = useState('')

  const { data: prompts = [], isLoading } = usePrompts()
  const createPrompt = useCreatePrompt()
  const activatePrompt = useActivatePrompt()
  const deletePrompt = useDeletePrompt()

  // Find active prompt
  const activePrompt = prompts.find(p => p.isActive)

  const handleCreateNew = () => {
    // Pre-fill with active prompt text
    if (activePrompt) {
      setNewPromptText(activePrompt.promptText)
    }
    setNewPromptComment('')
    setIsCreating(true)
  }

  const handleCreatePrompt = async () => {
    if (newPromptText.trim().length < 10) {
      alert('Le prompt doit contenir au moins 10 caractères')
      return
    }

    await createPrompt.mutateAsync({
      promptText: newPromptText.trim(),
      comment: newPromptComment.trim() || undefined
    })

    // Reset form
    setNewPromptText('')
    setNewPromptComment('')
    setIsCreating(false)
  }

  const handleActivatePrompt = async (promptId: string) => {
    await activatePrompt.mutateAsync(promptId)
  }

  const handleDeletePrompt = async (promptId: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce prompt ? Cette action est irréversible.')) {
      await deletePrompt.mutateAsync(promptId)
    }
  }

  const handleStartEditingActive = () => {
    if (activePrompt) {
      setEditedPromptText(activePrompt.promptText)
      setEditedPromptComment(activePrompt.comment || '')
      setIsEditingActive(true)
    }
  }

  const handleCancelEditingActive = () => {
    setIsEditingActive(false)
    setEditedPromptText('')
    setEditedPromptComment('')
  }

  const handleSaveEditedPrompt = async () => {
    if (editedPromptText.trim().length < 10) {
      alert('Le prompt doit contenir au moins 10 caractères')
      return
    }

    // Create a new prompt version with the edited text
    await createPrompt.mutateAsync({
      promptText: editedPromptText.trim(),
      comment: editedPromptComment.trim() || undefined
    })

    // Reset editing state
    setIsEditingActive(false)
    setEditedPromptText('')
    setEditedPromptComment('')
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="bg-white rounded-2xl shadow-md border-2 border-gray-200 overflow-hidden transition-all duration-300">
      {/* FOCUS MODE: Creating new prompt */}
      {isCreating ? (
        <div className="animate-scale-in">
          {/* Compact Header */}
          <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-b-2 border-purple-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-full flex items-center justify-center">
                <PlusIcon className="h-4 w-4 text-white" />
              </div>
              <span className="text-sm font-bold text-purple-900">Création d'un nouveau prompt</span>
            </div>
            <button
              onClick={() => {
                setIsCreating(false)
                setNewPromptText('')
                setNewPromptComment('')
              }}
              className="text-xs text-purple-600 hover:text-purple-800 underline font-medium"
            >
              Annuler
            </button>
          </div>

          {/* Create Form - EXPANDED */}
          <div className="p-6 bg-gradient-to-br from-purple-50/30 to-white">
            <div className="mb-4 p-3 bg-purple-100 border border-purple-200 rounded-lg">
              <p className="text-xs text-purple-800 flex items-center gap-2">
                <SparklesIcon className="h-4 w-4" />
                <span className="font-medium">Le prompt actif a été pré-rempli. Modifiez-le selon vos besoins.</span>
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Texte du prompt *
                </label>
                <textarea
                  value={newPromptText}
                  onChange={(e) => setNewPromptText(e.target.value)}
                  rows={12}
                  placeholder="Entrez le texte du prompt pour la génération d'appréciation..."
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm transition-all"
                />
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-gray-500">
                    Minimum 10 caractères - Décrivez le contexte et les instructions pour l'IA
                  </p>
                  <span className={`text-xs font-semibold ${newPromptText.length < 10 ? 'text-red-600' : 'text-green-600'}`}>
                    {newPromptText.length} caractères
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Commentaire (optionnel)
                </label>
                <input
                  type="text"
                  value={newPromptComment}
                  onChange={(e) => setNewPromptComment(e.target.value)}
                  placeholder="Décrivez les changements ou la raison de ce nouveau prompt..."
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleCreatePrompt}
                  disabled={createPrompt.isPending || newPromptText.trim().length < 10}
                  className="flex-1 inline-flex justify-center items-center px-5 py-3 border-2 border-transparent text-sm font-bold rounded-xl text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all"
                >
                  {createPrompt.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Création...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-5 w-5 mr-2" />
                      Créer et Activer
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setIsCreating(false)
                    setNewPromptText('')
                    setNewPromptComment('')
                  }}
                  className="px-5 py-3 border-2 border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 transition-all"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* NORMAL MODE: Prompt list */}
          <div className="p-5 bg-gradient-to-r from-purple-50 to-indigo-50 border-b-2 border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <SparklesIcon className="h-5 w-5 text-purple-600" />
                  Gestion des Prompts IA
                </h2>
                <p className="text-xs text-gray-600 mt-1">
                  Gérez les prompts pour la génération d'appréciations
                </p>
              </div>
              <button
                onClick={handleCreateNew}
                className="inline-flex items-center px-4 py-2 border-2 border-transparent text-sm font-bold rounded-xl text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-md hover:shadow-lg transition-all"
              >
                <PlusIcon className="h-4 w-4 mr-1.5" />
                Nouveau
              </button>
            </div>
          </div>

          <div className="p-5">
            {/* Prompts List */}
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                <span className="ml-3 text-gray-600 font-medium">Chargement...</span>
              </div>
            ) : prompts.length === 0 ? (
              <p className="text-center py-6 text-gray-500 text-sm">Aucun prompt disponible</p>
            ) : (
              <div className="space-y-4">
                {/* Active Prompt - Always Shown */}
                {activePrompt && (
                  <div>
                    <div className="rounded-xl border-2 border-green-400 bg-gradient-to-br from-green-50 to-emerald-50 shadow-md">
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-green-500 text-white shadow-sm">
                              <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />
                              ACTIF
                            </span>
                            {!isEditingActive && activePrompt.comment && (
                              <span className="text-sm font-semibold text-gray-800">
                                {activePrompt.comment}
                              </span>
                            )}
                          </div>
                          {!isEditingActive && (
                            <button
                              onClick={handleStartEditingActive}
                              className="text-xs px-3 py-1.5 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-all"
                            >
                              Modifier
                            </button>
                          )}
                        </div>

                        {/* Prompt Text - Editable */}
                        {isEditingActive ? (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Texte du prompt *
                              </label>
                              <textarea
                                value={editedPromptText}
                                onChange={(e) => setEditedPromptText(e.target.value)}
                                rows={12}
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm transition-all"
                              />
                              <div className="flex items-center justify-between mt-2">
                                <p className="text-xs text-gray-500">
                                  Minimum 10 caractères
                                </p>
                                <span className={`text-xs font-semibold ${editedPromptText.length < 10 ? 'text-red-600' : 'text-green-600'}`}>
                                  {editedPromptText.length} caractères
                                </span>
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Commentaire (optionnel)
                              </label>
                              <input
                                type="text"
                                value={editedPromptComment}
                                onChange={(e) => setEditedPromptComment(e.target.value)}
                                placeholder="Décrivez les changements..."
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                              />
                            </div>

                            <div className="flex gap-3 pt-2">
                              <button
                                onClick={handleSaveEditedPrompt}
                                disabled={createPrompt.isPending || editedPromptText.trim().length < 10}
                                className="flex-1 inline-flex justify-center items-center px-5 py-3 border-2 border-transparent text-sm font-bold rounded-xl text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all"
                              >
                                {createPrompt.isPending ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Sauvegarde...
                                  </>
                                ) : (
                                  <>
                                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                                    Sauver et Activer
                                  </>
                                )}
                              </button>
                              <button
                                onClick={handleCancelEditingActive}
                                className="px-5 py-3 border-2 border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 transition-all"
                              >
                                Abandonner
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="mb-2">
                              <div className="text-sm text-gray-700 whitespace-pre-wrap bg-white/70 p-3 rounded-lg border border-gray-200">
                                {activePrompt.promptText}
                              </div>
                            </div>

                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <ClockIcon className="h-3.5 w-3.5" />
                                {formatDate(activePrompt.createdAt)}
                              </span>
                              <span className="font-medium">{activePrompt.createdByName}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Show History Button - After Active Prompt */}
                    {prompts.length > 1 && (
                      <div className="mt-4">
                        <button
                          onClick={() => setShowHistory(!showHistory)}
                          className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-semibold rounded-lg border-2 border-purple-200 text-purple-700 hover:bg-purple-50 transition-all"
                        >
                          {showHistory ? (
                            <>
                              <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                              Masquer l'historique
                            </>
                          ) : (
                            <>
                              <ClockIcon className="w-4 h-4 mr-1.5" />
                              Afficher l'historique ({prompts.length - 1})
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Historical Prompts - Only Shown When Toggle is Active */}
                {showHistory && (
                  <div className="space-y-3">
                    {prompts.filter(prompt => !prompt.isActive).map((prompt) => {
                    return (
                      <div
                        key={prompt.id}
                        className={`rounded-xl border-2 transition-all ${
                          prompt.isActive
                            ? 'border-green-400 bg-gradient-to-br from-green-50 to-emerald-50 shadow-md'
                            : selectedPromptId === prompt.id
                            ? 'border-purple-400 bg-purple-50 shadow-sm'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        {/* Header - Always Visible */}
                        <div className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              {prompt.isActive && (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-green-500 text-white shadow-sm">
                                  <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />
                                  ACTIF
                                </span>
                              )}
                              {prompt.comment && (
                                <span className="text-sm font-semibold text-gray-800">
                                  {prompt.comment}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {!prompt.isActive && (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleActivatePrompt(prompt.id)
                                    }}
                                    disabled={activatePrompt.isPending}
                                    className="text-xs px-3 py-1.5 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-all"
                                  >
                                    Activer
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleDeletePrompt(prompt.id)
                                    }}
                                    disabled={deletePrompt.isPending}
                                    className="text-xs p-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 disabled:opacity-50 transition-all"
                                    title="Supprimer"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Prompt Text - Always Full, Never Truncated */}
                          <div className="mb-2">
                            <div className="text-sm text-gray-700 whitespace-pre-wrap bg-white/70 p-3 rounded-lg border border-gray-200">
                              {prompt.promptText}
                            </div>
                          </div>

                          {/* Footer */}
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <ClockIcon className="h-3.5 w-3.5" />
                              {formatDate(prompt.createdAt)}
                            </span>
                            <span className="font-medium">{prompt.createdByName}</span>
                          </div>
                        </div>
                      </div>
                    )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
